import React, { useState } from 'react';
import { Button } from '@/components';
import { DocumentArrowDownIcon, CheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { proposalPdfService, ProposalPdfData } from '@/services/proposalPdfService';
import { Project } from '@/features/Projects/models/project.types';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ProposalPhaseProps {
  project: Project;
  onNext: () => void;
}

export default function ProposalPhase({ project, onNext }: ProposalPhaseProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [proposalData, setProposalData] = useState<Partial<ProposalPdfData>>({
    client: {
      name: '',
      contact: '',
      email: '',
      company: '',
    },
    team: [
      {
        name: 'Tech Lead Senior',
        role: 'Líder Técnico',
        experience: '8+ anos em desenvolvimento full-stack e arquitetura de sistemas',
      },
      {
        name: 'Desenvolvedor Full-Stack',
        role: 'Desenvolvedor Pleno',
        experience: '5+ anos em desenvolvimento web moderno e APIs RESTful',
      },
    ],
    timeline: [],
    investment: {
      totalValue: project.estimatedPrice || 0,
      paymentTerms: '50% no início do projeto e 50% na entrega final',
      validUntil: format(addDays(new Date(), 30), 'dd/MM/yyyy', { locale: ptBR }),
    },
    nextSteps: [
      'Aprovação da proposta pelo cliente',
      'Assinatura do contrato de prestação de serviços',
      'Kickoff meeting para alinhamento inicial',
      'Início do desenvolvimento conforme cronograma',
    ],
  });

  React.useEffect(() => {
    // Gerar timeline baseado nos milestones do projeto
    if (project.timeline?.milestones) {
      const timelineData = project.timeline.milestones.map((milestone) => ({
        phase: milestone.title,
        duration: `${milestone.estimatedDuration} dias`,
        description: milestone.description,
      }));
      setProposalData(prev => ({ ...prev, timeline: timelineData }));
    }
  }, [project]);

  const handleClientInfoChange = (field: string, value: string) => {
    setProposalData(prev => ({
      ...prev,
      client: {
        ...prev.client!,
        [field]: value,
      },
    }));
  };

  const handleInvestmentChange = (field: string, value: string | number) => {
    setProposalData(prev => ({
      ...prev,
      investment: {
        ...prev.investment!,
        [field]: value,
      },
    }));
  };

  const generateProposal = async () => {
    if (!proposalData.client?.name) {
      toast.error('Por favor, preencha o nome do cliente');
      return;
    }

    setIsGenerating(true);

    try {
      const completeData: ProposalPdfData = {
        project,
        client: proposalData.client!,
        team: proposalData.team!,
        timeline: proposalData.timeline!,
        investment: proposalData.investment!,
        nextSteps: proposalData.nextSteps!,
      };

      const pdfBlob = await proposalPdfService.generateProposalPdf(completeData);
      
      // Download do PDF
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposta-${project.name.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Proposta PDF gerada com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar proposta:', error);
      toast.error('Erro ao gerar proposta PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  const canProceed = project.resources && project.resources.length > 0 && project.estimatedPrice;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-raro-gray-900 mb-2">
            Geração de Proposta Comercial
          </h3>
          <p className="text-sm text-raro-gray-600">
            Configure os dados do cliente e gere a proposta comercial em PDF
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {canProceed ? (
            <div className="flex items-center text-green-600">
              <CheckIcon className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Pronto para gerar</span>
            </div>
          ) : (
            <div className="flex items-center text-amber-600">
              <ClockIcon className="w-5 h-5 mr-1" />
              <span className="text-sm font-medium">Aguardando recursos</span>
            </div>
          )}
        </div>
      </div>

      {!canProceed && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-700">
            <strong>Atenção:</strong> Para gerar a proposta, é necessário completar a fase de Recursos 
            com estimativas de equipe e preço total.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações do Cliente */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-semibold text-raro-gray-900 mb-4">
            Informações do Cliente
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-1">
                Nome do Cliente *
              </label>
              <input
                type="text"
                value={proposalData.client?.name || ''}
                onChange={(e) => handleClientInfoChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Nome ou razão social"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-1">
                Empresa
              </label>
              <input
                type="text"
                value={proposalData.client?.company || ''}
                onChange={(e) => handleClientInfoChange('company', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Nome da empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-1">
                Contato
              </label>
              <input
                type="text"
                value={proposalData.client?.contact || ''}
                onChange={(e) => handleClientInfoChange('contact', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Nome do contato"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-1">
                E-mail
              </label>
              <input
                type="email"
                value={proposalData.client?.email || ''}
                onChange={(e) => handleClientInfoChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="email@cliente.com"
              />
            </div>
          </div>
        </div>

        {/* Configurações de Investimento */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-semibold text-raro-gray-900 mb-4">
            Investimento
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-1">
                Valor Total (R$)
              </label>
              <input
                type="number"
                value={proposalData.investment?.totalValue || 0}
                onChange={(e) => handleInvestmentChange('totalValue', Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
              {project.estimatedPrice && (
                <p className="text-xs text-gray-500 mt-1">
                  Estimativa baseada nos recursos: {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(project.estimatedPrice)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-1">
                Condições de Pagamento
              </label>
              <textarea
                value={proposalData.investment?.paymentTerms || ''}
                onChange={(e) => handleInvestmentChange('paymentTerms', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Ex: 50% no início, 50% na entrega"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-raro-gray-700 mb-1">
                Proposta válida até
              </label>
              <input
                type="text"
                value={proposalData.investment?.validUntil || ''}
                onChange={(e) => handleInvestmentChange('validUntil', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="dd/mm/aaaa"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Preview */}
      {proposalData.timeline && proposalData.timeline.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-semibold text-raro-gray-900 mb-4">
            Cronograma (Preview)
          </h4>
          <div className="space-y-3">
            {proposalData.timeline.map((phase, index) => (
              <div key={index} className="flex items-start space-x-3 text-sm">
                <div className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-medium text-xs">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-raro-gray-900">{phase.phase}</div>
                  <div className="text-raro-gray-600">Duração: {phase.duration}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <div className="text-sm text-raro-gray-600">
          {canProceed ? 
            'Todos os dados necessários foram preenchidos' : 
            'Complete a fase de recursos para continuar'
          }
        </div>

        <div className="flex items-center space-x-3">
          <Button
            onClick={generateProposal}
            disabled={!canProceed || isGenerating || !proposalData.client?.name}
            className="flex items-center space-x-2"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            <span>{isGenerating ? 'Gerando PDF...' : 'Gerar Proposta PDF'}</span>
          </Button>

          <Button
            onClick={onNext}
            variant="secondary"
            disabled={!canProceed}
          >
            Próxima Fase
          </Button>
        </div>
      </div>
    </div>
  );
}